import React, { useEffect, useState } from "react";
import { Alert, Avatar, Badge, Box, Button, Chip, Divider, LinearProgress, Skeleton, Stack, Tab, Tabs, Typography } from "@mui/material";
import PhoneIcon from "@mui/icons-material/PhoneOutlined";
import PlaceIcon from "@mui/icons-material/PlaceOutlined";
import HistoryIcon from "@mui/icons-material/HistoryOutlined";
import ReceiptIcon from "@mui/icons-material/ReceiptLongOutlined";
import NotesIcon from "@mui/icons-material/StickyNote2Outlined";
import SmsIcon from "@mui/icons-material/ForumOutlined";
import InfoIcon from "@mui/icons-material/InfoOutlined";
import TaskIcon from "@mui/icons-material/AssignmentLateOutlined";
import { toast } from "react-toastify";
import { getData, postData } from "../../../apis/api";
import { ENDPOINTS } from "../../../apis/urls";
import CallNotes from "./CallNotes";

const API = ENDPOINTS.CALLS;

export const initials = (name) => (name || "?").trim().split(/\s+/).slice(0, 2).map((part) => part[0]).join("").toUpperCase();
const day = (value) => {
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? "" : date.toLocaleDateString([], { month: "short", day: "numeric", year: "numeric" });
};
const money = (order) => [order.currency, order.total].filter(Boolean).join(" ");

/** The caller's identity block: the one thing that must render instantly. */
export function CallerIdentity({ caller, fallbackName, fallbackNumber, size = 48 }) {
  const name = caller?.name || fallbackName || "Unknown caller";
  const number = caller?.number || fallbackNumber || "";
  return <Stack direction="row" spacing={1.5} alignItems="center" sx={{ minWidth: 0 }}>
    <Avatar sx={{ width: size, height: size, fontSize: size / 2.6, bgcolor: "rgba(255,255,255,0.22)", color: "inherit", fontWeight: 600 }}>
      {initials(name)}
    </Avatar>
    <Box sx={{ minWidth: 0 }}>
      <Typography variant="h6" noWrap sx={{ fontWeight: 600, lineHeight: 1.2 }}>{name}</Typography>
      <Stack direction="row" spacing={1} alignItems="center" sx={{ opacity: 0.9, mt: 0.25, flexWrap: "wrap" }}>
        {number && <Stack direction="row" spacing={0.5} alignItems="center"><PhoneIcon sx={{ fontSize: 14 }} /><Typography variant="body2">{number}</Typography></Stack>}
        {caller?.city && <Stack direction="row" spacing={0.5} alignItems="center"><PlaceIcon sx={{ fontSize: 14 }} /><Typography variant="body2">{caller.city}</Typography></Stack>}
      </Stack>
    </Box>
  </Stack>;
}

/** Chips that answer "have we met this person before?" without a click. */
export function CallerFacts({ caller, dense = false }) {
  const facts = [];
  if (caller?.totalCalls) facts.push({ icon: <HistoryIcon sx={{ fontSize: 14 }} />, label: `${caller.totalCalls} previous call${caller.totalCalls === 1 ? "" : "s"}` });
  if (caller?.openTaskCount) facts.push({ icon: <TaskIcon sx={{ fontSize: 14 }} />, label: `${caller.openTaskCount} open task${caller.openTaskCount === 1 ? "" : "s"}`, color: "warning" });
  if (caller?.lastCallAt) facts.push({ icon: null, label: `Last called ${day(caller.lastCallAt)}` });
  if (!facts.length) facts.push({ icon: null, label: "First time caller" });
  return <Stack direction="row" spacing={0.75} sx={{ flexWrap: "wrap", rowGap: 0.75 }}>
    {facts.map((fact) => <Chip key={fact.label} icon={fact.icon || undefined} label={fact.label}
      size="small" color={fact.color || "default"} variant={dense ? "outlined" : "filled"}
      sx={{ height: 24, bgcolor: fact.color ? undefined : "rgba(255,255,255,0.18)", color: "inherit", "& .MuiChip-icon": { color: "inherit" } }} />)}
  </Stack>;
}

/** Why the bot is handing over — the sentence the agent reads before speaking. */
export function CallReason({ brief, teaser }) {
  const summary = brief?.thisCall?.summary || teaser;
  return <Box sx={{ borderLeft: 3, borderColor: "primary.main", pl: 1.5, py: 0.5 }}>
    <Typography variant="overline" color="text.secondary" sx={{ lineHeight: 1.6 }}>Why they’re calling</Typography>
    {summary
      ? <Typography variant="body2" sx={{ fontWeight: 500 }}>{summary}</Typography>
      : <Skeleton width="80%" height={20} />}
    {brief?.thisCall?.reason && brief.thisCall.reason !== summary &&
      <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.25 }}>Bot noted: {brief.thisCall.reason}</Typography>}
  </Box>;
}

function Empty({ children }) {
  return <Stack alignItems="center" spacing={0.5} sx={{ py: 3, opacity: 0.7 }}>
    <InfoIcon fontSize="small" />
    <Typography variant="caption" align="center">{children}</Typography>
  </Stack>;
}

function PreviousCalls({ brief }) {
  const calls = brief?.history?.calls || [];
  if (brief?.history?.loading && !calls.length) return <Stack spacing={1}>{[0, 1, 2].map((i) => <Skeleton key={i} height={52} />)}</Stack>;
  if (!calls.length) return <Empty>{brief?.history?.note || "No earlier calls from this number."}</Empty>;
  return <Stack spacing={1}>
    {brief?.history?.latestSummary && <Alert severity="info" icon={false} sx={{ py: 0.5 }}>
      <Typography variant="caption">{brief.history.latestSummary}</Typography>
    </Alert>}
    {calls.map((call) => <Box key={call.id} sx={{ p: 1.25, borderRadius: 1.5, bgcolor: "action.hover" }}>
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.25, flexWrap: "wrap" }}>
        <Typography variant="caption" sx={{ fontWeight: 600 }}>{day(call.created_at)}</Typography>
        {call.callType && <Chip size="small" label={call.callType} sx={{ height: 20 }} />}
        {call.disposition && <Typography variant="caption" color="text.secondary">{call.disposition}</Typography>}
        {call.satisfactionScore != null && <Typography variant="caption" color="text.secondary">· CSAT {call.satisfactionScore > 5 ? `${call.satisfactionScore}%` : `${call.satisfactionScore}/5`}</Typography>}
      </Stack>
      <Typography variant="body2">{call.journeySummary || "No summary recorded."}</Typography>
    </Box>)}
  </Stack>;
}

function Orders({ brief, callId }) {
  const records = brief?.orders?.records || [];
  if (brief?.orders?.loading && !records.length) return <Stack spacing={1}>{[0, 1].map((i) => <Skeleton key={i} height={64} />)}</Stack>;
  if (!records.length) return <Stack spacing={1} alignItems="center" sx={{ py: 2 }}>
    <Empty>{brief?.orders?.note || "No orders found for this number."}</Empty>
    {brief?.orders?.retryable && <Button size="small" onClick={() => postData(API.HANDOVER_BRIEF(callId), {})
      .then(() => toast.info("Retrying the store lookup…")).catch(() => toast.error("Retry unavailable"))}>Retry store lookup</Button>}
  </Stack>;
  return <Stack spacing={1}>
    {brief?.orders?.note && <Typography variant="caption" color="text.secondary">{brief.orders.note}</Typography>}
    {records.map((order) => <Box key={order.id} sx={{ p: 1.25, borderRadius: 1.5, border: 1, borderColor: "divider" }}>
      <Stack direction="row" justifyContent="space-between" spacing={1} sx={{ flexWrap: "wrap" }}>
        <Typography variant="body2" sx={{ fontWeight: 600 }}>#{order.number}</Typography>
        <Typography variant="body2">{money(order)}</Typography>
      </Stack>
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 0.5, flexWrap: "wrap" }}>
        {order.status && <Chip size="small" label={order.status} sx={{ height: 20 }} />}
        {order.date && <Typography variant="caption" color="text.secondary">{day(order.date)}</Typography>}
      </Stack>
      {!!(order.items || []).length && <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.5 }}>
        {order.items.map((item) => `${item.quantity} × ${item.name}`).join(", ")}
      </Typography>}
      {!!(order.tracking || []).length && <Typography variant="caption" sx={{ display: "block", mt: 0.25 }}>
        {order.tracking.map((item) => item.summary || item.tracking_number || item.status).filter(Boolean).join(" · ")}
      </Typography>}
    </Box>)}
  </Stack>;
}

function RingCentral({ callId }) {
  const [history, setHistory] = useState(null);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const results = await Promise.allSettled([
        getData(`calls/call/${callId}/ringcentral-history`, { kind: "messages" }),
        getData(`calls/call/${callId}/ringcentral-history`, { kind: "calls" }),
      ]);
      if (cancelled) return;
      const [messages, calls] = results;
      setHistory({
        messages: messages.value?.Content?.records || [],
        calls: calls.value?.Content?.records || [],
        note: results.some((item) => item.status === "rejected") ? "Some RingCentral history is unavailable." : "",
      });
    })();
    return () => { cancelled = true; };
  }, [callId]);
  if (!history) return <Stack spacing={1}>{[0, 1, 2].map((i) => <Skeleton key={i} height={34} />)}</Stack>;
  const rows = [
    ...history.messages.map((message, i) => ({ key: `m${i}`, at: message.occurredAt, text: message.body || "(no text)" })),
    ...history.calls.map((call, i) => ({ key: `c${i}`, at: call.startTime, text: `${call.direction || ""} call · ${call.result || ""}` })),
  ];
  if (!rows.length) return <Empty>{history.note || "No RingCentral texts or calls for this number."}</Empty>;
  return <Stack spacing={0.75}>
    {history.note && <Typography variant="caption" color="warning.main">{history.note}</Typography>}
    {rows.map((row) => <Box key={row.key} sx={{ p: 1, borderRadius: 1.5, bgcolor: "action.hover" }}>
      <Typography variant="caption" color="text.secondary">{row.at ? new Date(row.at).toLocaleString() : ""}</Typography>
      <Typography variant="body2">{row.text}</Typography>
    </Box>)}
  </Stack>;
}

/**
 * Everything known about the caller, in tabs rather than a stack of accordions
 * so the popup stays one screen and each panel can fail on its own.
 */
export default function CallerBrief({ brief, teaser, callId, handoverId, height = 260, initialTab = 0 }) {
  const [tab, setTab] = useState(initialTab);
  useEffect(() => { setTab(initialTab); }, [initialTab, callId]);
  const counts = {
    calls: (brief?.history?.calls || []).length,
    orders: (brief?.orders?.records || []).length,
    tasks: (brief?.history?.openTasks || []).length,
  };
  const tabs = [
    { label: "Overview", icon: <InfoIcon sx={{ fontSize: 16 }} /> },
    { label: "Calls", icon: <HistoryIcon sx={{ fontSize: 16 }} />, count: counts.calls },
    { label: "Orders", icon: <ReceiptIcon sx={{ fontSize: 16 }} />, count: counts.orders },
    { label: "Notes", icon: <NotesIcon sx={{ fontSize: 16 }} /> },
    { label: "Texts", icon: <SmsIcon sx={{ fontSize: 16 }} /> },
  ];
  return <Box>
    <Tabs value={tab} onChange={(_, next) => setTab(next)} variant="scrollable" scrollButtons={false}
          sx={{ minHeight: 38, "& .MuiTab-root": { minHeight: 38, py: 0, fontSize: 13, textTransform: "none" } }}>
      {tabs.map((item) => <Tab key={item.label} icon={item.count
        ? <Badge badgeContent={item.count} color="primary" sx={{ "& .MuiBadge-badge": { fontSize: 9, height: 14, minWidth: 14 } }}>{item.icon}</Badge>
        : item.icon} iconPosition="start" label={item.label} />)}
    </Tabs>
    <Divider />
    <Box sx={{ height, overflowY: "auto", pt: 1.25, pr: 0.5 }}>
      {tab === 0 && <Stack spacing={1.25} divider={<Divider flexItem />}>
        <CallReason brief={brief} teaser={teaser} />
        {!!(brief?.history?.openTasks || []).length && <Box>
          <Typography variant="overline" color="text.secondary">Open tasks</Typography>
          {brief.history.openTasks.map((task) => <Stack key={task.id} direction="row" spacing={1} alignItems="center">
            <Chip size="small" color={task.priority === "urgent" ? "error" : "default"} label={task.priority} sx={{ height: 20 }} />
            <Typography variant="body2">{task.title}</Typography>
          </Stack>)}
        </Box>}
        {!!(brief?.thisCall?.toolsUsed || []).length && <Box>
          <Typography variant="overline" color="text.secondary">What the bot already did</Typography>
          <Stack direction="row" spacing={0.5} sx={{ flexWrap: "wrap", rowGap: 0.5 }}>
            {brief.thisCall.toolsUsed.map((tool, i) => <Chip key={`${tool.name}${i}`} size="small" variant="outlined" label={tool.name} sx={{ height: 22 }} />)}
          </Stack>
        </Box>}
        {brief?.history?.latestSummary && <Box>
          <Typography variant="overline" color="text.secondary">Last time they called</Typography>
          <Typography variant="body2">{brief.history.latestSummary}</Typography>
        </Box>}
        {!brief && <LinearProgress />}
      </Stack>}
      {tab === 1 && <PreviousCalls brief={brief} />}
      {tab === 2 && <Orders brief={brief} callId={callId} />}
      {tab === 3 && <CallNotes callId={callId} handoverId={handoverId} emptyHint="Notes you take now stay on this call — the team sees them afterwards." />}
      {tab === 4 && <RingCentral callId={callId} />}
    </Box>
  </Box>;
}
